import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, fmtMoney, fmtDate, StatusBadge, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CreditCard, Link as LinkIcon, Copy, Trash } from "@phosphor-icons/react";

export default function Payments() {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: txs = [], refetch } = useQuery({ queryKey: ["txs"], queryFn: async () => (await api.get("/payments/transactions")).data });

  const del = useMutation({
    mutationFn: async (session_id) => (await api.delete(`/payments/transactions/${session_id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["txs"] }); toast.success("Transaction deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || "—";

  const createLink = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!description.trim()) return toast.error("Add a description");
    setBusy(true);
    try {
      const r = await api.post("/payments/adhoc-checkout", {
        amount: amt,
        description,
        client_id: clientId || null,
        origin_url: window.location.origin,
      });
      setLastLink({ url: r.data.checkout_url, session_id: r.data.session_id, amount: amt, description });
      setAmount(""); setDescription(""); setClientId("");
      refetch();
      toast.success("Payment link created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(lastLink.url);
    toast.success("Link copied");
  };

  return (
    <div data-testid="payments-page">
      <PageHeader
        eyebrow="Stripe"
        title="Payments"
        description="Create one-off payment links for anything outside of a standard invoice — deposits, add-ons, retainers."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* Ad-hoc link builder */}
        <div className="card-flat p-5" data-testid="adhoc-form">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={18} weight="duotone" className="text-blue-800" />
            <div className="font-display font-semibold text-slate-900">New payment link</div>
          </div>
          <div className="space-y-4">
            <Field label="Amount (USD)">
              <Input type="number" step="0.01" placeholder="250.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adhoc-amount" />
            </Field>
            <Field label="Description (shown to payer)">
              <Input placeholder="Deposit for USCIS interview 3/12" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="adhoc-description" />
            </Field>
            <Field label="Client (optional)">
              <Select value={clientId || "__none__"} onValueChange={(v) => setClientId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="adhoc-client"><SelectValue placeholder="No client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No client</SelectItem>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Button onClick={createLink} disabled={busy} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="adhoc-create-btn">
              <LinkIcon size={14} weight="bold" className="mr-1.5" /> {busy ? "Creating…" : "Create Stripe link"}
            </Button>
          </div>

          {lastLink && (
            <div className="mt-5 border border-emerald-200 bg-emerald-50 rounded-md p-3" data-testid="adhoc-last-link">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Link ready</div>
              <div className="text-xs text-slate-700 mt-1 truncate font-mono">{lastLink.url}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-adhoc-link">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
                <Button size="sm" onClick={() => window.open(lastLink.url, "_blank")} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="open-adhoc-link">
                  Open checkout
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-slate-900">Stripe transactions</div>
              <div className="text-xs text-slate-500 mt-0.5">All checkouts initiated from the platform</div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{txs.length} total</div>
          </div>
          <div className="overflow-x-auto">
            {txs.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">No transactions yet — create a link or pay an invoice.</div>
            ) : (
              <table className="w-full text-sm" data-testid="txs-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Kind</th>
                    <th className="text-left px-3 py-3 font-semibold">Description</th>
                    <th className="text-left px-3 py-3 font-semibold">Client</th>
                    <th className="text-right px-3 py-3 font-semibold">Amount</th>
                    <th className="text-left px-3 py-3 font-semibold">Status</th>
                    <th className="text-left px-5 py-3 font-semibold">Created</th>
                    <th className="px-3 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.session_id} className="data-row border-t border-slate-100">
                      <td className="px-5 py-3 text-slate-700 capitalize">{t.kind}</td>
                      <td className="px-3 py-3 text-slate-700 text-xs">
                        {t.kind === "invoice" ? `Invoice ${t.invoice_id?.slice(-6)}` : t.description}
                      </td>
                      <td className="px-3 py-3 text-slate-700">{t.client_id ? clientName(t.client_id) : "—"}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtMoney(t.amount)}</td>
                      <td className="px-3 py-3"><StatusBadge status={t.payment_status === "pending" ? "pending" : t.payment_status === "paid" ? "paid" : t.payment_status} /></td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(t.created_at)}</td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          data-testid={`delete-tx-${t.session_id}`}
                          onClick={() => {
                            if (window.confirm(`Delete this ${t.kind} transaction (${fmtMoney(t.amount)})? This only removes the record here — it does NOT refund the payment in Stripe.`)) {
                              del.mutate(t.session_id);
                            }
                          }}
                          className="text-slate-400 hover:text-red-600 p-1 rounded"
                          title="Delete transaction record"
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
